import { useState, useEffect, useCallback } from 'react';
import { queueHomeRequest } from '../homeRequestQueue';

export interface UrgentWorkIdCountFilter {
  foremanAreaId?: number;
  serviceAreaId?: number;
}

interface UseUrgentWorkIdCountReturn {
  count: number;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
}

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Fetches count of Work IDs that are New or Approved and have priority Medium or High.
 * Uses same area filter as other home Work ID widgets.
 */
export function useUrgentWorkIdCount(
  filter?: UrgentWorkIdCountFilter | null
): UseUrgentWorkIdCountReturn {
  const [count, setCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = new URLSearchParams();
      if (filter?.foremanAreaId != null) {
        params.append('foremanAreaId', String(filter.foremanAreaId));
      }
      if (filter?.serviceAreaId != null) {
        params.append('serviceAreaId', String(filter.serviceAreaId));
      }
      const queryString = params.toString();
      const url = `${BASE_URL}/work-ids/urgent-count${queryString ? `?${queryString}` : ''}`;
      const response = await queueHomeRequest(() => fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      }));
      if (!response.ok) {
        throw new Error(`Failed to fetch urgent work ID count: ${response.statusText}`);
      }
      const json = await response.json();
      setCount(typeof json.count === 'number' ? json.count : 0);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch urgent work ID count'));
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [filter?.foremanAreaId, filter?.serviceAreaId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    count,
    loading,
    error,
    refetch: fetchData,
  };
}
